/**
 * **The booking grid's whole reading of one visible range** — the entry point the calendar hook
 * calls once, so the combination of sessions, working hours and blocks is a decision a test can
 * reach rather than wiring in a component.
 *
 * Pure: no React, no DOM, no query hook, no i18next.
 */

import type {
  TrainWithMeModulesBookingDtosResponsesTrainerAvailabilityResponse as TrainerAvailabilityResponse,
  TrainWithMeModulesTrainingDtosResponsesCalendarEventResponse as CalendarEventResponse,
} from '~/shared/api/generated/model';

import { isDrawn, isLiveAt, readBookingSession, type BookingSession } from './bookingSession';
import type { VisibleRange } from './calendarRange';
import { daysBetween, type CivilDay } from './trainerClock';
import {
  businessHoursOf,
  dayNoteOf,
  trainerBlocks,
  workingDays,
  type BusinessHoursEntry,
  type DayNote,
  type TrainerBlock,
  type WorkingDay,
} from './workingHours';

/** A session as the grid draws it. */
export interface GridSession {
  readonly session: BookingSession;
  /** Wearing the running ring. */
  readonly live: boolean;
}

export interface BookingCalendar {
  /** Every occurrence the read returned inside the range, called-off ones included. */
  readonly sessions: readonly BookingSession[];
  /** What the grid draws: not called off, and standing at an hour. */
  readonly grid: readonly GridSession[];
  readonly businessHours: readonly BusinessHoursEntry[];
  readonly blocks: readonly TrainerBlock[];
  /** The day header's second line, per visible day. */
  readonly dayNotes: ReadonlyMap<CivilDay, DayNote>;
  /** The trainer's windows on each visible day — what a new session is checked against. */
  readonly working: ReadonlyMap<CivilDay, WorkingDay>;
  /** Nothing holds an hour anywhere in the range — a valid state, drawn as an empty grid. */
  readonly isEmpty: boolean;
}

interface BookingCalendarInput {
  readonly events: readonly CalendarEventResponse[];
  readonly availability: TrainerAvailabilityResponse;
  readonly range: VisibleRange;
  readonly zone: string;
  readonly nowMs: number;
}

export function bookingCalendar({
  events,
  availability,
  range,
  zone,
  nowMs,
}: BookingCalendarInput): BookingCalendar {
  const days = daysBetween(range.first, range.last);
  // The schedule read is a day wider at each end (`scheduleWindow`), so the edges are trimmed on
  // the trainer's clock rather than trusted.
  const sessions = events
    .map((event) => readBookingSession(event, zone))
    .filter((session) => session.day !== null && session.day >= range.first && session.day <= range.last);
  const grid = sessions
    .filter(isDrawn)
    .map((session) => ({ session, live: isLiveAt(session, nowMs) }));
  const working = workingDays(availability, days);
  const blocks = trainerBlocks(availability);

  return {
    sessions,
    grid,
    businessHours: businessHoursOf(working),
    blocks,
    dayNotes: new Map(working.map((day) => [day.day, dayNoteOf(day, blocks, zone)])),
    working: new Map(working.map((day) => [day.day, day])),
    isEmpty: grid.length === 0,
  };
}
